import { useMemo, useState } from "react";
import {
  ConfiguratorStepper,
  ImageDisplay,
  ImageDisplayImageProps,
  ModalWrapper,
  StepWrapper,
  ToggleComponent,
} from "src/pages/configurator/components";
import {
  ComparePackageOptionsContent,
  ExpiredOptionsSwitch,
  NoOptions,
  OptionListContainer,
} from "src/pages/configurator/components/options";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { FlexibleOptionCard, getSelectedStructuralOptionCodes } from "~components";
import { Css } from "~generated/css";
import { CheckoutConfigOptionGroupStep } from "~generated/graphqlTypes";
import { fail, useComputed, useTestIds } from "~utils";
import { UserWarning } from "../components/UserWarning";
import { getCutoffDaysRemaining, getFloorPlanImages, optionGroupFilter } from "../Configurator.utils";
import { InteriorStep_CheckoutOptionGroupFragment } from "./Interior.step.generated";

export function StructuralStep() {
  const { toggleOption, checkoutConfig, formState, selectedOptionIds } = useConfiguratorContext();
  const tid = useTestIds({}, "options-step");
  // We only have 2 sub-steps for options
  const [showExpiredOptions, setShowExpiredOptions] = useState(false);
  // used to determine whether image or floor plan is shown (toggle)
  const [viewFloorPlan, setViewFloorPlan] = useState(true);
  const [lastToggledOptionId, setLastToggledOptionId] = useState<string | null>(null);
  const [compareCCOG, setCompareCCOG] = useState<InteriorStep_CheckoutOptionGroupFragment | undefined>(undefined);

  const { canEdit, canChangeProductOffering } = checkoutConfig ?? {};
  const isDisabled = !canEdit?.allowed;
  const isPastStructuralCutoff = checkoutConfig ? !canChangeProductOffering?.allowed : false;

  const { floorPlanOptions, filteredFloorPlanOptions, alwaysShowExpiredOptions } = useMemo(() => {
    const optionsByStep = checkoutConfig?.optionGroups.groupBy((ccog) => ccog.step);
    const floorPlanOptions = optionsByStep?.[CheckoutConfigOptionGroupStep.OptionsStructural] ?? [];

    // When we are on the floor plan step and the product offering is not editable (analog for also blocking structural changes),
    // we default to showing expired options in a read only state as they will all be expired. This step is skipped in the normal flow,
    // but a user may manually return to it from the header link or the summary page.
    const alwaysShowExpiredOptions = isPastStructuralCutoff;

    return {
      floorPlanOptions,
      filteredFloorPlanOptions: floorPlanOptions.filter(
        optionGroupFilter(showExpiredOptions || alwaysShowExpiredOptions, selectedOptionIds),
      ),
      alwaysShowExpiredOptions,
    };
  }, [checkoutConfig, showExpiredOptions, isPastStructuralCutoff, selectedOptionIds]);

  // Get option image for left <ImageDisplay>
  const optionImages: ImageDisplayImageProps[] = useMemo(() => {
    if (lastToggledOptionId) {
      return [
        {
          id: lastToggledOptionId,
          kind: "image",
          imageUrl: filteredFloorPlanOptions
            .find((o) => o.id === lastToggledOptionId)
            ?.options.find((o) => o.id === lastToggledOptionId)?.option.optionImage?.largePreview,
        },
      ];
    }

    return [
      {
        id: filteredFloorPlanOptions.first?.options.first?.option.id,
        kind: "image",
        imageUrl: filteredFloorPlanOptions.first?.options.first?.option.optionImage?.largePreview,
      },
    ];
  }, [lastToggledOptionId, filteredFloorPlanOptions]);

  // Check if there are valid option images (not undefined)
  const hasValidOptionImages = useMemo(() => {
    return optionImages.some((image) => {
      if (image.kind === "image") {
        return image.imageUrl;
      }
      return false;
    });
  }, [optionImages]);

  const someOptionsHaveExpired = floorPlanOptions.some(
    (ccog) => ccog.cutoffDate && getCutoffDaysRemaining(ccog.cutoffDate) <= 0,
  );

  /**
   *
   * Handle Interactive Floor Plans
   *
   * 1) Initial floor plan determined based on sort order of images when entering step regardless of what (if any) options are chosen
   *  - Note: we could instead show the floor plan that has a selected option (if at least one is chosen) if desired
   * 2) Upon an option selection, highlight the floor plan(s) that has the selected option
   *  - if the selected option isn't currently displayed on the floor plan, change the floor plan to show
   *    the next floor plan that has the selected option
   * 3) Upon de-selection of an option, remove the highlight the floor plan(s) that has the de-selected option
   *  - if the de-selected option isn't currently displayed on the floor plan, change the floor plan to show
   *    the next floor plan that has the de-selected option
   */

  // access the floor plan images
  const planFloorPlans: ImageDisplayImageProps[] = useMemo(
    () => getFloorPlanImages(checkoutConfig?.productOffering, checkoutConfig?.isReversed),
    [checkoutConfig?.productOffering, checkoutConfig?.isReversed],
  );

  // used to determine which floor plan to show - initially based on response (i.e. image sortOrder)
  const [visibleFloorIndex, setVisibleFloorIndex] = useState<number>(0);
  // passed into floor plan component to highlight selected options
  const selectedStructuralOptionCodes = useMemo(
    () => getSelectedStructuralOptionCodes(checkoutConfig?.optionGroups ?? [], selectedOptionIds),
    [checkoutConfig, selectedOptionIds],
  );

  const loading = useComputed(() => formState.loading, [formState]);

  return (
    <>
      <StepWrapper
        hideBorder
        loading={loading}
        loadingView="personalization"
        title="Floor Plan Modifications"
        description="Modify your home's layout and add built-in features to suit your lifestyle."
        warningNode={
          !loading &&
          (isDisabled || isPastStructuralCutoff) && (
            <UserWarning
              title="The floor plan can no longer be modified."
              disabledReasons={[canEdit, canChangeProductOffering]}
            />
          )
        }
        left={
          <>
            {hasValidOptionImages && (
              // toggle container
              <div css={Css.z1.absolute.topPx(24).left("50%").add("transform", "translate(-50%)").$}>
                <ToggleComponent
                  offLabel="Option"
                  onLabel="Floor Plans"
                  value={viewFloorPlan}
                  onClick={() => setViewFloorPlan(!viewFloorPlan)}
                  {...tid.toggle}
                />
              </div>
            )}
            {viewFloorPlan && planFloorPlans.length > 0 ? (
              <ImageDisplay
                images={planFloorPlans}
                selectedOptionCodes={selectedStructuralOptionCodes}
                imageIndex={visibleFloorIndex}
                onClick={setVisibleFloorIndex}
                isReversed={!!checkoutConfig?.isReversed}
                {...tid.imageDisplay}
              />
            ) : !viewFloorPlan && hasValidOptionImages ? (
              <ImageDisplay images={optionImages} {...tid.imageDisplay} />
            ) : null}
          </>
        }
        right={
          <>
            {someOptionsHaveExpired && !alwaysShowExpiredOptions && (
              <ExpiredOptionsSwitch
                showExpiredOptions={showExpiredOptions}
                setShowExpiredOptions={setShowExpiredOptions}
              />
            )}
            <OptionListContainer>
              {filteredFloorPlanOptions.isEmpty && <NoOptions showExpiredOptions={showExpiredOptions} />}
              {filteredFloorPlanOptions.map((ccog) => {
                return (
                  <FlexibleOptionCard
                    key={ccog.id}
                    ccfgOptionGroup={ccog}
                    toggleOptionSideEffects={(_, cco) => {
                      setLastToggledOptionId(cco.id);
                    }}
                    {...tid.flexibleOptionCard}
                  />
                );
              })}
            </OptionListContainer>
          </>
        }
      />
      <ConfiguratorStepper />
      <ModalWrapper isOpen={!!compareCCOG} title={compareCCOG?.group.name} close={() => setCompareCCOG(undefined)}>
        {compareCCOG && checkoutConfig && (
          <ComparePackageOptionsContent
            checkoutConfigId={checkoutConfig.id}
            rpogId={compareCCOG.group.id}
            ccog={compareCCOG}
            selectedOptionIds={selectedOptionIds}
            onSelect={(selectedOptionId) =>
              toggleOption(
                compareCCOG,
                compareCCOG.options.find((cco) => cco.id === selectedOptionId) ?? fail(`Missing ${selectedOptionId}`),
              )
            }
          />
        )}
      </ModalWrapper>
    </>
  );
}
